import './Drawer.scss';
import cn from 'classnames';
import { PropsWithChildren, ReactNode, startTransition, useState, ViewTransition } from 'react';
import { MdClose } from 'react-icons/md';
import { Appearance, Design } from '../designConstants';
import { IconButton } from '../IconButton';

// The floating corner toggle. A controlled host that renders its own opener (e.g. a button in
// the page header) passes `hideToggle` instead, and drops the toggle props it would not use.
type DrawerToggleProps =
  | { toggleIcon: ReactNode; toggleAriaLabel: string; hideToggle?: false }
  | { hideToggle: true; toggleIcon?: never; toggleAriaLabel?: never };

type DrawerProps = PropsWithChildren<DrawerToggleProps & DrawerOptionProps>;

interface DrawerOptionProps {
  title: ReactNode;
  // Lets multiple floating drawer toggles stack in the same corner instead of overlapping —
  // e.g. the cross-channel agent drawer sits above the Help Center button on pages where both
  // can appear.
  bottomOffset?: number;
  // Reports every open/close transition (the toggle click AND the header close button) — never
  // called on mount, since nothing has transitioned yet. A host that needs to know whether the
  // panel is CURRENTLY open — not just react to the moment it opens, since it may already be open
  // when something else it cares about changes — gets this. Optional and additive.
  onOpenChange?: (open: boolean) => void;
  // Optional CONTROLLED open state. Omit it and the drawer owns its own (the original behavior,
  // which every existing host still relies on). Pass it and the host owns it, which is what lets
  // something outside the drawer — a button on another page — open the panel. Pair it with
  // `onOpenChange`, or the toggle will have nothing to write back to.
  open?: boolean;
}

// A floating corner toggle + a panel that slides open by animating its own width, pushing
// sibling content over rather than covering it (mount as a flex sibling of the content it
// should push — see AgentDrawer/CrossChannelAgentDrawer for the reference layout). The toggle
// and panel are both always mounted; only the panel's width and the toggle's visibility change,
// so state inside `children` (e.g. an in-progress chat session) survives closing and reopening.
export const Drawer = ({
  title,
  toggleIcon,
  toggleAriaLabel,
  hideToggle = false,
  bottomOffset = 24,
  onOpenChange,
  open: controlledOpen,
  children
}: DrawerProps): ReactNode => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;

  // Both lanes fire on every transition: `onOpenChange` so a controlled host can store the new
  // value, and the internal setter so an uncontrolled host keeps working unchanged. Writing the
  // internal state while controlled is harmless — `open` above ignores it.
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    startTransition(() => setUncontrolledOpen(next));
  };

  return (
    <>
      <ViewTransition>
        {!open && !hideToggle && (
          <IconButton
            className='drawer-toggle'
            aria-label={toggleAriaLabel}
            style={{ bottom: bottomOffset }}
            onClick={() => setOpen(true)}
          >
            {toggleIcon}
          </IconButton>
        )}
      </ViewTransition>
      <aside className={cn('drawer', { open })}>
        <div className='drawer-header'>
          <span className='drawer-title'>{title}</span>
          <IconButton
            aria-label='Close'
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            onClick={() => setOpen(false)}
          >
            <MdClose />
          </IconButton>
        </div>
        <div className='drawer-body'>{children}</div>
      </aside>
    </>
  );
};
